import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trophy, Package, Users, RefreshCw, TrendingUp, TrendingDown, Target, Settings2, Truck, Fuel, MessageCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const medal = (i) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`);
const fmt = (n) => Number(n || 0).toLocaleString('en-ZA');
const fmtR = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PERIODS = [
  { key: 'today',  label: 'Today' },
  { key: 'week',   label: 'This Week' },
  { key: 'month',  label: 'This Month' },
  { key: 'custom', label: 'Custom' },
];

export default function FleetTrips() {
  const [search, setSearch] = useSearchParams();
  const [period, setPeriod] = useState(search.get('period') || 'week');
  const [dateFrom, setDateFrom] = useState(search.get('from') || '');
  const [dateTo, setDateTo] = useState(search.get('to') || '');
  const [driverId, setDriverId] = useState('all');
  const [vehicle, setVehicle] = useState('all');

  const [analytics, setAnalytics] = useState(null);
  const [collective, setCollective] = useState(null);
  const [driverOpts, setDriverOpts] = useState([]);
  const [vehicleOpts, setVehicleOpts] = useState([]);
  const [target, setTarget] = useState(80);
  const [loading, setLoading] = useState(true);

  const [showTargetDialog, setShowTargetDialog] = useState(false);
  const [newTarget, setNewTarget] = useState('80');

  const fetchOptions = useCallback(async () => {
    try {
      const [d, v] = await Promise.all([
        axios.get(`${API}/driver-trips/driver-options`),
        axios.get(`${API}/driver-trips/vehicle-options`),
      ]);
      setDriverOpts(d.data || []);
      setVehicleOpts(v.data?.registrations || []);
    } catch { /* silent */ }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') {
        if (!dateFrom || !dateTo) { setLoading(false); return; }
        params.date_from = dateFrom;
        params.date_to = dateTo;
      }
      if (driverId && driverId !== 'all') params.driver_id = driverId;
      if (vehicle && vehicle !== 'all') params.vehicle = vehicle;
      const [a, c] = await Promise.all([
        axios.get(`${API}/driver-trips/analytics`, { params }),
        axios.get(`${API}/driver-trips/collective-history?months=12`),
      ]);
      setAnalytics(a.data);
      setCollective(c.data);
      setTarget(a.data?.target?.daily_stops_target || 80);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [period, dateFrom, dateTo, driverId, vehicle]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);
  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  // Reflect period in the URL so navigation back from driver-detail keeps state
  useEffect(() => {
    const q = { period };
    if (period === 'custom') {
      if (dateFrom) q.from = dateFrom;
      if (dateTo) q.to = dateTo;
    }
    setSearch(q, { replace: true });
  }, [period, dateFrom, dateTo, setSearch]);

  const saveTarget = async () => {
    const t = Number(newTarget);
    if (!t || t <= 0) { toast.error('Target must be > 0'); return; }
    try {
      await axios.put(`${API}/driver-trips/target`, { daily_stops_target: t });
      setTarget(t); setShowTargetDialog(false);
      toast.success('Target updated');
      fetchAnalytics();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const rows = analytics?.rows || [];
  const col = analytics?.collective || null;
  const rankings = analytics?.rankings || null;

  const bestId = rankings?.best?.driver_id;
  const lowestId = rankings?.lowest?.driver_id;

  const rangeLabel = useMemo(() => {
    if (!analytics?.filters) return '';
    const { date_from, date_to } = analytics.filters;
    return date_from === date_to ? date_from : `${date_from} → ${date_to}`;
  }, [analytics]);

  const driverLink = useCallback((did) => {
    const q = new URLSearchParams();
    q.set('period', period);
    if (period === 'custom') { q.set('from', dateFrom); q.set('to', dateTo); }
    return `/admin/fleet-trips/driver/${did}?${q.toString()}`;
  }, [period, dateFrom, dateTo]);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package className="h-6 w-6 text-indigo-600" /> Fleet Trips — Collective View
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              One-stop dashboard: fleet performance, per-driver breakdown, rankings — filtered any way you like.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { setNewTarget(String(target)); setShowTargetDialog(true); }} data-testid="target-config-btn">
              <Settings2 className="h-3 w-3 mr-1" /> Target · {target}/day
            </Button>
            <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={loading}>
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <Card data-testid="filters-card">
          <CardContent className="p-3 flex flex-wrap items-end gap-3">
            <div className="flex flex-wrap gap-1">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPeriod(p.key)}
                  className={`px-3 py-1.5 text-xs rounded ${period === p.key ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                  data-testid={`period-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {period === 'custom' && (
              <>
                <div>
                  <Label className="text-[11px]">From</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-40" data-testid="date-from" />
                </div>
                <div>
                  <Label className="text-[11px]">To</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-40" data-testid="date-to" />
                </div>
              </>
            )}
            <div>
              <Label className="text-[11px]">Driver</Label>
              <Select value={driverId} onValueChange={setDriverId}>
                <SelectTrigger className="h-8 w-56" data-testid="driver-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="all">All Drivers</SelectItem>
                  {driverOpts.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.full_name || d.username}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Vehicle</Label>
              <Select value={vehicle} onValueChange={setVehicle}>
                <SelectTrigger className="h-8 w-40" data-testid="vehicle-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="all">All Vehicles</SelectItem>
                  {vehicleOpts.map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[11px] text-muted-foreground">Range</div>
              <div className="text-xs font-mono">{rangeLabel || '—'}</div>
            </div>
          </CardContent>
        </Card>

        {/* Collective KPI tiles */}
        {col && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kpi-tiles">
            <KpiTile label="Active Drivers" value={col.active_drivers} icon={<Users className="h-4 w-4 text-indigo-600" />} />
            <KpiTile label="Days Reported" value={col.days_reported} icon={<Package className="h-4 w-4 text-slate-500" />} />
            <KpiTile label="Total Stops" value={fmt(col.total_stops)} icon={<Target className="h-4 w-4 text-emerald-600" />} />
            <KpiTile label="Total KM" value={fmt(col.kilometres)} icon={<Truck className="h-4 w-4 text-blue-600" />} />
            <KpiTile label="Fuel Spend" value={fmtR(col.fuel_total)} icon={<Fuel className="h-4 w-4 text-orange-600" />} />
          </div>
        )}

        {/* Rankings summary */}
        {rankings && (rankings.best || rankings.lowest) && (
          <Card data-testid="rankings-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Trophy className="h-4 w-4 text-amber-500" /> Rankings — {rangeLabel}
              </CardTitle>
              <CardDescription className="text-xs">
                Target: <b>{target}</b> stops/day · Fleet total: <b className="text-indigo-700">{fmt(col?.total_stops)}</b>
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {rankings.best && (
                <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3" data-testid="rank-best">
                  <div className="text-xs uppercase font-semibold text-emerald-800 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Best Performer</div>
                  <div className="text-lg font-bold text-emerald-900 mt-1">{rankings.best.driver_name}</div>
                  <div className="text-xs text-emerald-800">{fmt(rankings.best.total_stops)} stops · {rankings.best.performance_pct}%</div>
                </div>
              )}
              {rankings.lowest && rankings.lowest.driver_id !== rankings.best?.driver_id && (
                <div className="rounded-lg border-2 border-rose-200 bg-rose-50 p-3" data-testid="rank-lowest">
                  <div className="text-xs uppercase font-semibold text-rose-800 flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Lowest Performer</div>
                  <div className="text-lg font-bold text-rose-900 mt-1">{rankings.lowest.driver_name}</div>
                  <div className="text-xs text-rose-800">{fmt(rankings.lowest.total_stops)} stops · {rankings.lowest.performance_pct}%</div>
                </div>
              )}
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/60 p-3" data-testid="rank-meeting">
                <div className="text-xs uppercase font-semibold text-emerald-800">Meeting Target</div>
                <div className="text-3xl font-extrabold text-emerald-900">{rankings.meeting_target_count}</div>
                <div className="text-xs text-emerald-700">of {rows.length} driver(s)</div>
              </div>
              <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-3" data-testid="rank-below">
                <div className="text-xs uppercase font-semibold text-amber-800">Below Target</div>
                <div className="text-3xl font-extrabold text-amber-900">{rankings.below_target_count}</div>
                <div className="text-xs text-amber-700">of {rows.length} driver(s)</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fuel Efficiency Ranking */}
        {rankings?.efficiency_ranking?.length > 0 && (
          <Card data-testid="efficiency-ranking-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Fuel className="h-4 w-4 text-orange-600" /> Fuel Efficiency (km / R) — {rangeLabel}
              </CardTitle>
              <CardDescription className="text-xs">
                How far each driver went per Rand of fuel. Higher is better. Distance uses odometer readings from shifts where available.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {rankings.fuel_leader && (
                <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3" data-testid="fuel-leader">
                  <div className="text-xs uppercase font-semibold text-emerald-800 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Most Efficient</div>
                  <div className="text-lg font-bold text-emerald-900 mt-1">{rankings.fuel_leader.driver_name}</div>
                  <div className="text-sm text-emerald-800">
                    <b>{rankings.fuel_leader.km_per_rand}</b> km/R
                    <span className="text-xs text-emerald-700 ml-2">
                      · {fmt(rankings.fuel_leader.distance_km)} km on {fmtR(rankings.fuel_leader.fuel_total)}
                    </span>
                  </div>
                </div>
              )}
              {rankings.fuel_laggard && rankings.fuel_laggard.driver_id !== rankings.fuel_leader?.driver_id && (
                <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-3" data-testid="fuel-laggard">
                  <div className="text-xs uppercase font-semibold text-rose-800 flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Least Efficient</div>
                  <div className="text-lg font-bold text-rose-900 mt-1">{rankings.fuel_laggard.driver_name}</div>
                  <div className="text-sm text-rose-800">
                    <b>{rankings.fuel_laggard.km_per_rand}</b> km/R
                    <span className="text-xs text-rose-700 ml-2">
                      · {fmt(rankings.fuel_laggard.distance_km)} km on {fmtR(rankings.fuel_laggard.fuel_total)}
                    </span>
                  </div>
                </div>
              )}
              <div className="rounded-lg border-2 border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase font-semibold text-slate-700">Ranked Drivers</div>
                <div className="text-3xl font-extrabold text-slate-900">{rankings.efficiency_ranking.length}</div>
                <div className="text-xs text-slate-600">have both fuel + distance data</div>
              </div>
            </CardContent>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-xs" data-testid="efficiency-table">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="text-left px-3 py-2">Rank</th>
                    <th className="text-left px-3 py-2">Driver</th>
                    <th className="text-left px-3 py-2">Vehicle</th>
                    <th className="text-right px-3 py-2">Distance</th>
                    <th className="text-right px-3 py-2 text-orange-700">Fuel</th>
                    <th className="text-right px-3 py-2 text-emerald-700">km / R</th>
                    <th className="text-right px-3 py-2 text-slate-700">R / km</th>
                  </tr>
                </thead>
                <tbody>
                  {rankings.efficiency_ranking.map((e, i) => (
                    <tr key={e.driver_id} className={`border-t ${i === 0 ? 'bg-emerald-50/40' : i === rankings.efficiency_ranking.length - 1 && rankings.efficiency_ranking.length > 1 ? 'bg-rose-50/40' : ''}`} data-testid={`efficiency-row-${i}`}>
                      <td className="px-3 py-2 font-bold">{medal(i)}</td>
                      <td className="px-3 py-2 font-semibold">{e.driver_name}</td>
                      <td className="px-3 py-2 font-mono">
                        {e.vehicle
                          ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-200">🚚 {e.vehicle}</span>
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="text-right px-3 py-2">
                        {fmt(e.distance_km)}
                        <span className="text-[9px] text-slate-500 ml-1">({e.distance_source})</span>
                      </td>
                      <td className="text-right px-3 py-2 text-orange-700">{fmtR(e.fuel_total)}</td>
                      <td className="text-right px-3 py-2 font-extrabold text-emerald-700">{e.km_per_rand}</td>
                      <td className="text-right px-3 py-2 text-slate-700">{fmtR(e.rand_per_km)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Per-driver table */}
        <Card data-testid="driver-breakdown-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-indigo-600" /> Driver Breakdown
            </CardTitle>
            <CardDescription className="text-xs">
              {driverId !== 'all'
                ? 'Showing selected driver'
                : `All drivers ranked by total stops (${rangeLabel})`}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="text-center py-6 text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">No parcel entries for this filter.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">Rank</th>
                    <th className="text-left px-3 py-2">Driver</th>
                    <th className="text-left px-3 py-2">Vehicle</th>
                    <th className="text-center px-3 py-2">Days</th>
                    <th className="text-center px-3 py-2">Trips</th>
                    <th className="text-center px-3 py-2">KM</th>
                    <th className="text-center px-3 py-2 text-emerald-700">Delivered</th>
                    <th className="text-center px-3 py-2 text-indigo-700">Collected</th>
                    <th className="text-right px-3 py-2">Stops</th>
                    <th className="text-center px-3 py-2">Avg/Day</th>
                    <th className="text-center px-3 py-2 text-orange-700">Fuel</th>
                    <th className="text-center px-3 py-2 text-emerald-700">km/R</th>
                    <th className="text-right px-3 py-2">Perf %</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const isBest = r.driver_id === bestId;
                    const isLowest = rows.length > 1 && r.driver_id === lowestId;
                    const rowBg = isBest ? 'bg-emerald-50/60' : isLowest ? 'bg-rose-50/60' : (i < 3 ? 'bg-amber-50/40' : '');
                    return (
                      <tr key={r.driver_id} className={`border-t ${rowBg}`} data-testid={`driver-row-${i}`}>
                        <td className="px-3 py-2 font-bold">{medal(i)}</td>
                        <td className="px-3 py-2">
                          <Link
                            to={driverLink(r.driver_id)}
                            className="font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
                            data-testid={`driver-link-${r.driver_id}`}
                          >
                            {r.driver_name}
                          </Link>
                          {r.comments_count > 0 && (
                            <span
                              className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-indigo-700 bg-indigo-100 border border-indigo-200 rounded px-1"
                              title={`${r.comments_count} note${r.comments_count === 1 ? '' : 's'}`}
                              data-testid={`row-comments-badge-${r.driver_id}`}
                            >
                              <MessageCircle className="h-2.5 w-2.5" /> {r.comments_count}
                            </span>
                          )}
                          <div className="text-[10px] text-muted-foreground">{(r.role || '').replace('_', ' ')}</div>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {r.vehicle
                            ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-200">🚚 {r.vehicle}</span>
                            : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="text-center px-3 py-2">{r.days_worked}</td>
                        <td className="text-center px-3 py-2">{r.trips}</td>
                        <td className="text-center px-3 py-2">{fmt(r.kilometres)}</td>
                        <td className="text-center px-3 py-2 text-emerald-700 font-semibold">{fmt(r.delivered)}</td>
                        <td className="text-center px-3 py-2 text-indigo-700 font-semibold">{fmt(r.collections)}</td>
                        <td className="text-right px-3 py-2 font-extrabold">{fmt(r.total_stops)}</td>
                        <td className="text-center px-3 py-2">{r.avg_stops_per_day}</td>
                        <td className="text-center px-3 py-2 text-orange-700">{fmtR(r.fuel_total)}</td>
                        <td className="text-center px-3 py-2 text-emerald-700 font-semibold">
                          {r.km_per_rand > 0 ? r.km_per_rand : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="text-right px-3 py-2">
                          <Badge className={r.target_met ? 'bg-emerald-600' : 'bg-amber-500'}>
                            {r.performance_pct}%
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {col && (
                  <tfoot className="bg-slate-900 text-white font-bold" data-testid="collective-totals">
                    <tr>
                      <td className="px-3 py-2" colSpan={2}>Fleet Total</td>
                      <td className="text-center px-3 py-2">—</td>
                      <td className="text-center px-3 py-2">{col.days_reported}</td>
                      <td className="text-center px-3 py-2">{col.trips}</td>
                      <td className="text-center px-3 py-2">{fmt(col.kilometres)}</td>
                      <td className="text-center px-3 py-2 text-emerald-300">{fmt(col.delivered)}</td>
                      <td className="text-center px-3 py-2 text-indigo-300">{fmt(col.collections)}</td>
                      <td className="text-right px-3 py-2 text-amber-300">{fmt(col.total_stops)}</td>
                      <td className="text-center px-3 py-2">{col.avg_stops_per_day}</td>
                      <td className="text-center px-3 py-2 text-orange-300">{fmtR(col.fuel_total)}</td>
                      <td className="text-center px-3 py-2">—</td>
                      <td className="text-right px-3 py-2">—</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </CardContent>
        </Card>

        {/* 12-month history (retained from previous view) */}
        {collective && (
          <Card data-testid="collective-history-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-500" /> Fleet-wide 12-month history
              </CardTitle>
              <CardDescription className="text-xs">Every driver / delivery staff aggregated per month.</CardDescription>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="text-left px-2 py-1.5">Month</th>
                    <th className="text-center px-2 py-1.5">Active</th>
                    <th className="text-center px-2 py-1.5">Days</th>
                    <th className="text-center px-2 py-1.5">Taken</th>
                    <th className="text-center px-2 py-1.5 text-emerald-700">Delivered</th>
                    <th className="text-center px-2 py-1.5 text-indigo-700">Collected</th>
                    <th className="text-center px-2 py-1.5 text-rose-700">Returned</th>
                    <th className="text-right px-2 py-1.5">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {[...collective.rows].reverse().map((r) => (
                    <tr key={r.month} className="border-t" data-testid={`col-row-${r.month}`}>
                      <td className="px-2 py-1.5 font-mono">{r.month}</td>
                      <td className="text-center px-2 py-1.5">{r.active_drivers}</td>
                      <td className="text-center px-2 py-1.5">{r.days_reported}</td>
                      <td className="text-center px-2 py-1.5">{fmt(r.taken)}</td>
                      <td className="text-center px-2 py-1.5 text-emerald-700 font-semibold">{fmt(r.delivered)}</td>
                      <td className="text-center px-2 py-1.5 text-indigo-700 font-semibold">{fmt(r.collections)}</td>
                      <td className="text-center px-2 py-1.5 text-rose-700">{fmt(r.returned)}</td>
                      <td className="text-right px-2 py-1.5 font-extrabold">{fmt(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Target config dialog */}
        <Dialog open={showTargetDialog} onOpenChange={setShowTargetDialog}>
          <DialogContent data-testid="target-dialog">
            <DialogHeader>
              <DialogTitle>Daily Stops Target</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground">
              Every driver's performance % is measured against this target × days worked. Default is 80.
            </p>
            <Input
              type="number" min="1" step="1"
              value={newTarget}
              onChange={(e) => setNewTarget(e.target.value)}
              data-testid="target-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTargetDialog(false)}>Cancel</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveTarget} data-testid="target-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function KpiTile({ label, value, icon }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1">{icon}{label}</div>
        <div className="text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}
